import { tenantInfoMixin, currentUserInfoMixin } from "@/js/custom-vue-mixins";
import { get, set, cloneDeep } from "lodash";

export default {
    mixins: [tenantInfoMixin, currentUserInfoMixin],
    props: {
        meta: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            initialFieldValuesSet: {},
        };
    },
    computed: {
        metaComputed() {
            return Object.assign(
                {},
                this.$tenantInfo,
                this.$currentUserInfo,
                cloneDeep(this.meta)
            );
        },
    },
    methods: {
        isComponentField(field) {
            return !!field.component;
        },
        processField(field) {
            // do conditional_show first in case we don't need to process anymore
            field = this.processFieldConditionalShow(field);
            if (!field) return;

            if (!this.isComponentField(field)) {
                // fix for groups not inheriting initial values
                field = this.processValueInitial(field);
                field = this.processFieldMeta(field);
                field = this.processFieldFormMode(field);
                field = this.processFieldStyle(field);
            }

            // this is for components as well, in-case we want to set existing props from other values
            field = this.processFieldValueAsProp(field);

            // still need to check children even if it's just a component field
            if (field.children && Array.isArray(field.children)) {
                field.children = field.children
                    .map((child) =>
                        Object.assign(child, {
                            $parent: {
                                name: field.name,
                                $parent: field.$parent,
                            },
                        })
                    )
                    .map((child) => this.processField(child))
                    .filter((child) => !!child);
            }

            // remove internal props
            Object.keys(field)
                .filter((key) => key.startsWith("$"))
                .forEach((key) => this.$delete(field, key));

            return field;
        },
        getValueByKey(key) {
            // TODO: expand this to search by a single deeply nested key rather than having to use a path
            let data = {
                values: this.model,
                meta: this.metaComputed,
            };

            return get(data, key);
        },
        processFieldMeta(field) {
            const inputs = this.$formulate.options.library;
            const customInputs = Object.keys(inputs || {}).filter((key) => {
                return !inputs[key].component.startsWith("FormulateInput");
            });

            if (customInputs.includes(field.type)) {
                this.$set(field, "meta", this.metaComputed);
            }

            return field;
        },
        processFieldStyle(field) {
            let classNames = [
                "supplios-form-field",
                `supplios-form-field-${
                    field.layout || this.fieldLayout || "stacked"
                }`,
            ];

            let existingClassNames = field?.outer_class;

            if (existingClassNames) {
                if (Array.isArray(existingClassNames)) {
                    classNames = classNames.concat(existingClassNames);
                } else {
                    classNames.push(existingClassNames);
                }
            }

            this.$set(field, "outer_class", classNames);

            this.$delete(field, "layout");

            return field;
        },
        processFieldFormMode(field) {
            if (field?.$disabled === undefined) {
                this.$set(field, "$disabled", !!field.disabled);
            }

            if (this.mode === "preview") {
                this.$set(field, "disabled", true);
            } else {
                this.$set(field, "disabled", field.$disabled);
            }

            return field;
        },
        processFieldValueAsProp(field) {
            const value_as_prop = field?.value_as_prop;
            this.$delete(field, "value_as_prop");

            if (value_as_prop?.key) {
                // {
                //     "key": "meta.key" | "values.key",
                //     "prop": "fieldProp"
                // }
                const value = this.getValueByKey(value_as_prop.key);

                if (value && value !== "") {
                    this.$set(field, value_as_prop.prop, value);
                } else {
                    this.$delete(field, value_as_prop.prop);
                }
            }

            return field;
        },
        processFieldConditionalShow(field) {
            const conditional_show = field?.conditional_show;
            this.$delete(field, "conditional_show");

            if (conditional_show?.key) {
                // {
                //     "key": "meta.key" | "values.key",
                //     "value": true | false | 42 | "example",
                //     "show": true | false
                // }
                let value = this.getValueByKey(conditional_show.key);

                if (
                    conditional_show.value === undefined ||
                    conditional_show.value === null ||
                    conditional_show.value === "" ||
                    conditional_show.value === "false"
                ) {
                    conditional_show.value = false;
                } else if (conditional_show.value === "true") {
                    conditional_show.value = true;
                }

                if (
                    value === undefined ||
                    value === null ||
                    value === "" ||
                    value === []
                ) {
                    value = false;
                }

                if (conditional_show.value === value) {
                    if (!conditional_show.show) {
                        field = null;
                    }
                } else {
                    if (conditional_show.show) {
                        field = null;
                    }
                }
            }

            return field;
        },
        processFieldErrors(field) {
            if (field.error_behavior) {
                field["error-behavior"] = field.error_behavior;
            }

            return field;
        },

        getModelPath(field) {
            let names = [field.name];

            if (field.$parent) {
                names.push(0);
                names = names.concat(this.getModelPath(field.$parent));
            }

            return names;
        },
        // fix for groups not inheriting initial values
        processValueInitial(field) {
            if (field.value && !this.initialFieldValuesSet[field.name]) {
                const modelPath = this.getModelPath(field).reverse();
                set(this.model, modelPath, field.value);

                this.initialFieldValuesSet[field.name] = field.value;
            }

            return field;
        },
    },
};
